// useVersionState — exposes version check state for UI consumption.
//
// Polls /version.json and exposes whether an update is available.
// Used by useVersionCheck (toast + silent reload) and the Settings page.

import { useCallback, useEffect, useRef, useState } from "react";

declare const __APP_VERSION__: string;
declare const __COMMIT_SHA__: string;

interface VersionManifest {
  commitSha?: string;
  appVersion?: string;
  branch?: string;
  buildTime?: string;
}

export interface VersionState {
  currentVersion: string;
  currentSha: string;
  latestVersion: string | null;
  latestSha: string | null;
  isUpdateAvailable: boolean;
  isChecking: boolean;
  /** Timestamp of the last successful check, or null if never checked */
  lastChecked: number | null;
  checkNow: () => Promise<void>;
}

const POLL_MS = 5 * 60 * 1000;

export function useVersionState(): VersionState {
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [latestSha, setLatestSha] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isLocal = __COMMIT_SHA__ === "local";

  const check = useCallback(async () => {
    setIsChecking(true);
    const minDelay = new Promise((r) => setTimeout(r, 800));
    try {
      const [res] = await Promise.all([
        fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" }),
        minDelay,
      ]);
      if (!res.ok) {
        setLastChecked(Date.now());
        return;
      }
      const data = (await res.json()) as VersionManifest;
      if (data.commitSha) setLatestSha(data.commitSha);
      if (data.appVersion) setLatestVersion(data.appVersion);
      setLastChecked(Date.now());
    } catch {
      // Network hiccup or local dev 404 — not actionable.
      await minDelay;
      setLastChecked(Date.now());
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Background polling — only in deployed builds (local dev will 404)
  useEffect(() => {
    if (isLocal) return;

    check();
    intervalRef.current = setInterval(check, POLL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [check, isLocal]);

  const isUpdateAvailable =
    !isLocal && !!latestSha && latestSha !== __COMMIT_SHA__;

  return {
    currentVersion: __APP_VERSION__,
    currentSha: __COMMIT_SHA__,
    latestVersion,
    latestSha,
    isUpdateAvailable,
    isChecking,
    lastChecked,
    checkNow: check,
  };
}
